import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, XCircle, Trophy, Brain } from "lucide-react";
import { finalQuiz } from "../data/finalQuiz";

/**
 * FinalQuizSlide
 * 4 review questions. Selecting the correct option shows "Correct!".
 * Selecting a wrong option shows "Try again - pick another answer".
 * Wrong options become un-clickable so the kid keeps trying others.
 */
export const FinalQuizSlide = () => {
  // answers[qIndex] = { selectedIndices: number[], solved: boolean }
  const [answers, setAnswers] = useState(
    finalQuiz.map(() => ({ selectedIndices: [], solved: false }))
  );

  const handlePick = (qIdx, optIdx) => {
    const correctIdx = finalQuiz[qIdx].answer;
    setAnswers((prev) => {
      const next = [...prev];
      const cur = { ...next[qIdx] };
      if (cur.solved) return prev;
      if (cur.selectedIndices.includes(optIdx)) return prev;
      cur.selectedIndices = [...cur.selectedIndices, optIdx];
      if (optIdx === correctIdx) cur.solved = true;
      next[qIdx] = cur;
      return next;
    });
  };

  const solvedCount = answers.filter((a) => a.solved).length;

  return (
    <div className="slide-content" data-testid="final-quiz-slide">
      <div className="max-w-6xl w-full px-2">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-5"
        >
          <div className="inline-flex items-center gap-2 bg-white/90 rounded-full px-4 py-1.5 mb-3 border-2 border-purple-200">
            <Brain className="w-4 h-4 text-purple-600" />
            <span className="font-accent text-sm font-semibold text-slate-700">Quiz Time!</span>
          </div>
          <h2 className="font-heading font-bold text-3xl sm:text-5xl text-slate-900">
            Let&apos;s See What You <span style={{ color: "#FB923C" }}>Remember!</span> 🧠
          </h2>
          <p className="font-body text-slate-700 mt-2 text-base">
            Pick the right answer. If it&apos;s wrong, try again - pick another answer!
          </p>
          <div
            className="inline-flex items-center gap-2 mt-3 px-4 py-1.5 rounded-full bg-amber-100 border-2 border-amber-300"
            data-testid="quiz-score"
          >
            <Trophy className="w-4 h-4 text-amber-600" />
            <span className="font-heading font-bold text-amber-800 text-sm">
              Score: {solvedCount} / {finalQuiz.length}
            </span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {finalQuiz.map((q, qIdx) => {
            const state = answers[qIdx];
            return (
              <motion.div
                key={q.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: qIdx * 0.08 }}
                className="glass-card p-5"
                data-testid={`final-quiz-question-${qIdx}`}
              >
                <div className="flex items-start gap-3 mb-3">
                  <div
                    className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-heading font-bold text-white"
                    style={{
                      background: state.solved
                        ? "linear-gradient(135deg,#10B981,#059669)"
                        : "linear-gradient(135deg,#2563EB,#1D4ED8)",
                    }}
                  >
                    {qIdx + 1}
                  </div>
                  <p className="font-heading font-semibold text-base sm:text-lg text-slate-900 leading-snug">
                    {q.question}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {q.options.map((opt, i) => {
                    const wasPicked = state.selectedIndices.includes(i);
                    const isCorrect = i === q.answer;
                    const showCorrect = state.solved && isCorrect;
                    const showWrong = wasPicked && !isCorrect;
                    const disabled = state.solved || wasPicked;
                    let cls = "quiz-option";
                    if (showCorrect) cls += " correct";
                    else if (showWrong) cls += " wrong";
                    return (
                      <button
                        key={i}
                        className={cls}
                        onClick={() => handlePick(qIdx, i)}
                        disabled={disabled}
                        data-testid={`final-quiz-option-${qIdx}-${i}`}
                      >
                        <span className="font-heading mr-2">{String.fromCharCode(65 + i)}.</span>
                        {opt}
                      </button>
                    );
                  })}
                </div>

                <div className="h-7 mt-3">
                  <AnimatePresence mode="wait">
                    {state.solved && (
                      <motion.p
                        key="ok"
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="font-heading font-bold text-sm text-emerald-600 flex items-center gap-1.5"
                        data-testid={`final-quiz-feedback-correct-${qIdx}`}
                      >
                        <CheckCircle2 className="w-4 h-4" /> Correct! 🎉
                      </motion.p>
                    )}
                    {!state.solved && state.selectedIndices.length > 0 && (
                      <motion.p
                        key="try"
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="font-heading font-bold text-sm text-rose-600 flex items-center gap-1.5"
                        data-testid={`final-quiz-feedback-wrong-${qIdx}`}
                      >
                        <XCircle className="w-4 h-4" /> Try again - pick another answer!
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
